import { Component } from 'react';
import { asyncComponent, AsyncRouteProps } from '@jaredpalmer/after';
import { parse, stringifyUrl } from 'query-string';
import { basePath as checkoutBasePath } from './views/checkout/config';
import ScreenFetching from './components/screen/screen-fetching';
import ScreenError from './components/screen/screen-error';
import { EXECUTIVE_TEAM_PAGE_ENABLED } from './config';
import redirectStateCookie from './utils/redirect-state-cookie';
import { FAQ_BASE_URL } from './config';

import pick from 'lodash/fp/pick';

export const redirects: Array<{ from: string; to: string; state?: Record<string, any> }> = [
  {
    from: '/feedback',
    to: '/feedback/nps',
  },
  {
    from: '/legal',
    to: '/legal/terms-of-use',
  },
  {
    from: '/contact-us',
    to: '/contact-us/patients',
  },
  {
    from: '/medications',
    to: '/medications/popular',
  },
  {
    from: '/birthcontrol',
    to: '/treatments/birth-control',
  },
  {
    from: '/erectiledysfunction',
    to: '/treatments/erectile-dysfunction',
  },
  {
    from: '/cholesterol',
    to: '/treatments/cholesterol',
  },
  {
    from: '/pharmacies',
    to: '/participating-pharmacies',
  },
  {
    from: '/glucophage',
    to: '/metformin',
  },
  {
    from: '/glucophage-xr',
    to: '/metformin-er',
  },
  {
    from: '/rx-save',
    to: '/rxsave',
  },
  {
    from: '/m/about',
    to: '/about-us',
  },
  {
    from: '/quickstart',
    to: '/quicksave',
    state: {
      fromQuickStart: true,
    },
  }, // Moved from CloudFlare
  {
    from: '/signin',
    to: '/login',
  },
  {
    from: '/gift',
    to: 'https://docs.google.com/forms/d/e/1FAIpQLSe70CG5sx00tKivb9Uqw37wm2li-5AI9ar5_BVXq0OJeSwJtw/viewform',
  },
  {
    from: '/about-home-delivery',
    to: '/home-delivery',
  },
  {
    from: '/refills',
    to: '/',
  },
  {
    from: '/about/home-delivery',
    to: '/home-delivery',
  },
  {
    from: '/about/telehealth',
    to: '/',
  },
  {
    from: '/blink-and-insurance',
    to: `${FAQ_BASE_URL}/articles/360040796551-Does-Blink-accept-insurance-`,
  },
  {
    from: '/m/terms-of-use',
    to: '/legal/terms-of-use',
  },
  {
    from: '/m/privacy-policy',
    to: '/legal/privacy-policy',
  },
  {
    from: '/m/faq',
    to: FAQ_BASE_URL,
  },
  {
    from: '/faq/new-user',
    to: FAQ_BASE_URL,
  },
  {
    from: '/faq/member',
    to: FAQ_BASE_URL,
  },
  {
    from: '/m/legal/notice-of-privacy-practices',
    to: '/legal/hipaa-privacy-notice',
  },
  {
    from: '/ed',
    to: '/treatments/erectile-dysfunction',
  },
  {
    from: '/men',
    to: '/treatments/erectile-dysfunction',
  },
  {
    from: '/hair',
    to: '/propecia',
  },
];

const redirectComponent = ({ url, state }) =>
  class Redirect extends Component {
    static getInitialProps({ req, res }) {
      const query = req ? req.query : parse(location.search);

      if (res && state) {
        redirectStateCookie.set(res, state);
      }

      return {
        redirectTo: stringifyUrl({ url, query }),
      };
    }

    render() {
      return null;
    }
  };

const logicComponent = (fn) =>
  class Logic extends Component {
    static getInitialProps(ctx) {
      return fn(ctx);
    }

    render() {
      return null;
    }
  };

const asyncComponentWithLoader = (options) => asyncComponent({ ...options, Placeholder: ScreenFetching });

type Route = Omit<AsyncRouteProps, 'path'> & {
  path: string | string[];
  include?: boolean;
};

const redirectRoutes: Route[] = redirects.map(({ from, to, state }) => ({
  path: from,
  component: redirectComponent({
    url: to,
    state,
  }),
  exact: true,
}));

const routes: Route[] = [
  ...redirectRoutes,
  {
    path: '/',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-home',
      loader: () => import(/* webpackChunkName: "views-home" */ 'views/home'),
    }),
  },
  {
    path: '/(signup|login|logout|create-password|forgot-password|login|login/otp|guest-login)',
    component: asyncComponentWithLoader({
      chunkName: 'views-authentication',
      loader: () => import(/* webpackChunkName: "views-authentication" */ 'views/authentication-routes'),
    }),
  },
  {
    path: '/password-reset/:token',
    component: asyncComponentWithLoader({
      chunkName: 'views-authentication',
      loader: () => import(/* webpackChunkName: "views-authentication" */ 'views/authentication-routes'),
    }),
  },
  {
    path: '/magic-link/:token',
    component: asyncComponentWithLoader({
      chunkName: 'views-authentication',
      loader: () => import(/* webpackChunkName: "views-authentication" */ 'views/authentication-routes'),
    }),
  },
  {
    path: '/mobile/unavailable',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-mobile-unavailable',
      loader: () => import(/* webpackChunkName: "views-mobile-unavailable" */ 'views/service-unavailable'),
    }),
  },
  {
    path: '/mobile',
    component: asyncComponentWithLoader({
      chunkName: 'views-mobile-pages',
      loader: () => import(/* webpackChunkName: "views-mobile-pages" */ 'views/mobile-pages'),
    }),
  },
  {
    path: '/i/:code',
    component: logicComponent(async ({ match }) => {
      return {
        redirectTo: `/invite/${match.params.code}`,
      };
    }),
  },
  {
    path: '/invite/:code',
    component: asyncComponentWithLoader({
      chunkName: 'views-invite',
      loader: () => import(/* webpackChunkName: "views-invite" */ 'views/marketing/invited-by-friend'),
    }),
  },
  {
    path: '/not-found',
    component: ScreenError,
  },
  {
    path: '/better-rx-savings/:slug?',
    component: asyncComponentWithLoader({
      chunkName: 'views-better-rx-savings',
      loader: () => import(/* webpackChunkName: "views-better-rx-savings" */ 'views/better-rx-savings'),
    }),
  },
  {
    path: '/call',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-call',
      loader: () => import(/* webpackChunkName: "views-call" */ 'views/landing-pages/telesales-page'),
    }),
  },
  {
    path: '/about/delivery',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-about-delivery',
      loader: () => import(/* webpackChunkName: "views-about-delivery" */ 'views/about/delivery'),
    }),
  },
  {
    path: '/about/pickup',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-about-pickup',
      loader: () => import(/* webpackChunkName: "views-about-pickup" */ 'views/about/pickup'),
    }),
  },
  {
    path: '/telemed-restrictions',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-telemed-restrictions',
      loader: () => import(/* webpackChunkName: "views-telemed-restrictions" */ 'views/telemed-restrictions'),
    }),
  },
  {
    path: '/feedback/:surveyName',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-feedback',
      loader: () => import(/* webpackChunkName: "views-feedback" */ 'views/feedback'),
    }),
  },
  {
    path: '/redirect-to-new-homepage',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-redirect-to-new-homepage',
      loader: () => import(/* webpackChunkName: "views-redirect-to-new-homepage" */ 'views/sunset-redirect-page'),
    }),
  },
  {
    path: '/legal/:slug',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-legal',
      loader: () => import(/* webpackChunkName: "views-legal" */ 'views/legal'),
    }),
  },
  {
    path: '/m/legal/:slug',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-mobile-legal',
      loader: () => import(/* webpackChunkName: "views-mobile-legal" */ 'views/legal/legal-mobile'),
    }),
  },
  {
    path: '/contact-us/:slug',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-contact-us',
      loader: () => import(/* webpackChunkName: "views-contact-us" */ 'views/contact-us'),
    }),
  },
  {
    path: '/how-blink-works',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-how-blink-works',
      loader: () => import(/* webpackChunkName: "views-how-blink-works" */ 'views/how-blink-works'),
    }),
  },
  {
    path: '/price-match-guarantee',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-price-match-guarantee',
      loader: () => import(/* webpackChunkName: "views-price-match-guarantee" */ 'views/price-match-guarantee'),
    }),
  },
  {
    path: '/participating-pharmacies',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-participating-pharmacies',
      loader: () => import(/* webpackChunkName: "views-participating-pharmacies" */ 'views/participating-pharmacies'),
    }),
  },
  {
    path: '/home-delivery',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-home-delivery',
      loader: () => import(/* webpackChunkName: "views-home-delivery" */ 'views/marketing/medication-home-delivery'),
    }),
  },
  {
    path: '/online-rx',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-online-rx',
      loader: () => import(/* webpackChunkName: "views-online-rx" */ 'views/marketing/telehealth'),
    }),
  },
  {
    path: '/careers',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-careers',
      loader: () => import(/* webpackChunkName: "views-careers" */ 'views/careers'),
    }),
  },
  {
    path: '/careers/first-principles',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-careers-first-principles',
      loader: () => import(/* webpackChunkName: "views-careers-first-principles" */ 'views/careers/careers-principles'),
    }),
  },
  {
    path: '/careers/engineering',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-careers-engineering',
      loader: () => import(/* webpackChunkName: "views-careers-engineering" */ 'views/careers/careers-engineering'),
    }),
  },
  {
    path: '/careers/:department',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-careers-department',
      loader: () => import(/* webpackChunkName: "views-careers-department" */ 'views/careers/careers-department'),
    }),
  },
  {
    path: '/about-us',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-about-us',
      loader: () => import(/* webpackChunkName: "views-about-us" */ 'views/about-us'),
    }),
  },
  {
    path: '/executive-team',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-executive-team',
      loader: () => import(/* webpackChunkName: "views-executive-team" */ 'views/executive-team'),
    }),
    include: EXECUTIVE_TEAM_PAGE_ENABLED,
  },
  {
    path: '/executive-team/:slug',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-executive-team-member',
      loader: () => import(/* webpackChunkName: "views-executive-team-member" */ 'views/executive-team'),
    }),
    include: EXECUTIVE_TEAM_PAGE_ENABLED,
  },
  {
    path: '/medications/popular',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-medications-popular',
      loader: () =>
        import(
          /* webpackChunkName: "views-medications-popular" */
          'views/medication-directory/medication-directory-popular'
        ),
    }),
  },
  {
    path: '/medications/:letter',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-medications-letter',
      loader: () =>
        import(
          /* webpackChunkName: "views-medications-letter" */
          'views/medication-directory/medication-directory-by-letter'
        ),
    }),
  },
  {
    path: '/in-the-press',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-in-the-press',
      loader: () => import(/* webpackChunkName: "views-in-the-press" */ 'views/press'),
    }),
  },
  {
    path: '/testimonials',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-testimonials',
      loader: () => import(/* webpackChunkName: "views-testimonials" */ 'views/testimonials'),
    }),
  },
  {
    path: '/doctors',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-doctors',
      loader: () => import(/* webpackChunkName: "views-doctors" */ 'views/marketing/doctors'),
    }),
  },
  {
    path: '/pharmacists',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-pharmacists',
      loader: () => import(/* webpackChunkName: "views-pharmacists" */ 'views/marketing/pharmacists'),
    }),
  },
  {
    path: '/blog',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-blog',
      loader: () => import(/* webpackChunkName: "views-blog" */ 'views/blog'),
    }),
  },
  {
    path: '/blog/category/:slug',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-blog-category',
      loader: () => import(/* webpackChunkName: "views-blog-category" */ 'views/blog/blog-category'),
    }),
  },
  {
    path: '/(blog|blog/preview)/:slug',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-blog-post',
      loader: () => import(/* webpackChunkName: "views-blog-post" */ 'views/blog/blog-post'),
    }),
  },
  {
    path: '/blog/newsletter/:id/topics',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-blog-newsletter',
      loader: () => import(/* webpackChunkName: "views-blog-newsletter" */ 'views/blog/blog-newsletter-topics'),
    }),
  },
  {
    path: '/consultation/:consultationId',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-consultation',
      loader: () => import(/* webpackChunkName: "views-consultation" */ 'views/telemed'),
    }),
  },
  {
    path: '/treatments/:conditionSlug/:medSlug/:stepIndex',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-treatments-step',
      loader: () => import(/* webpackChunkName: "views-treatments-step" */ 'views/telemed/condition-flow'),
    }),
  },
  {
    path: '/treatments/:conditionName',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-treatments-condition',
      loader: () => import(/* webpackChunkName: "views-treatments-condition" */ 'views/telemed/cdp'),
    }),
  },
  {
    path: '/conditions/seasonal-allergies',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-conditions-allergies',
      loader: () => import(/* webpackChunkName: "views-conditions-allergies" */ 'views/seasonal-allergies'),
    }),
  },
  {
    path: '/(order-service|blink-pharmacy-plus|plus-meds|blinkrx)',
    component: asyncComponentWithLoader({
      chunkName: 'views-blink-pharmacy-plus',
      loader: () => import(/* webpackChunkName: "views-blink-pharmacy-plus" */ 'views/reverie'),
    }),
  },
  {
    path: '/(quicksave|quicksave10|quicksave20)',
    component: asyncComponentWithLoader({
      chunkName: 'views-quicksave',
      loader: () => import(/* webpackChunkName: "views-quicksave" */ 'views/magic-card'),
    }),
  },
  {
    path: '/qs/rf/:matchCode',
    component: logicComponent(async ({ match }) => {
      return {
        redirectTo: `/quicksave/rf?match_code=${match.params.matchCode}`,
      };
    }),
  },
  {
    path: '/qs/:matchCode',
    component: logicComponent(async ({ match }) => {
      return {
        redirectTo: `/quicksave?match_code=${match.params.matchCode}`,
      };
    }),
  },
  {
    path: '/pharmacy-confirmation',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-pharmacy-confirmation',
      loader: () => import(/* webpackChunkName: "views-pharmacy-confirmation" */ 'views/pharmacy-confirmation'),
    }),
  },
  {
    path: '/account',
    component: asyncComponentWithLoader({
      chunkName: 'views-account',
      loader: () => import('views/account'),
    }),
  },
  {
    path: checkoutBasePath,
    component: asyncComponentWithLoader({
      chunkName: 'views-checkout',
      loader: () => import(/* webpackChunkName: "views-checkout" */ 'views/checkout'),
    }),
  },
  {
    path: '/transfer',
    component: asyncComponentWithLoader({
      chunkName: 'views-transfer',
      loader: () => import(/* webpackChunkName: "views-transfer" */ 'views/pharmacy-transfer'),
    }),
  },
  {
    path: '/(men|men2)',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-lp-mens-health',
      loader: () => import(/* webpackChunkName: "views-lp-mens-health" */ 'views/landing-pages/mens-health'),
    }),
  },
  {
    path: '/ed',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-lp-erectile-dysfunction',
      loader: () =>
        import(/* webpackChunkName: "views-lp-erectile-dysfunction" */ 'views/landing-pages/erectile-dysfunction'),
    }),
  },
  {
    path: '/hair',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-lp-hair-loss',
      loader: () => import(/* webpackChunkName: "views-lp-hair-loss" */ 'views/landing-pages/hair-loss'),
    }),
  },
  {
    path: '/sildenafil20',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-lp-sildenafil',
      loader: () => import(/* webpackChunkName: "views-lp-sildenafil" */ 'views/landing-pages/sildenafil'),
    }),
  },
  {
    path: '/ana',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-marketing-ana',
      loader: () => import(/* webpackChunkName: "views-marketing-ana" */ 'views/marketing/ana'),
    }),
  },
  {
    path: '/free-first-order-promotion',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-marketing-free-first-order',
      loader: () =>
        import(/* webpackChunkName: "views-marketing-free-first-order" */ 'views/marketing/free-first-order'),
    }),
  },
  {
    path: '/holiday-2019',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-marketing-holiday-promo',
      loader: () => import(/* webpackChunkName: "views-marketing-holiday-promo" */ 'views/marketing/holiday-promo'),
    }),
  },
  {
    path: '/(philly|stl|houston|tampa)',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-telemed-redirect',
      loader: () => import(/* webpackChunkName: "views-telemed-redirect" */ 'views/telemed/components/telemedRedirect'),
    }),
  },
  {
    path: '/redirect-patient',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-redirect-patient',
      loader: () => import(/* webpackChunkName: "views-redirect-patient" */ 'views/redirect-patient'),
    }),
  },
  {
    path: '/specialty',
    component: asyncComponentWithLoader({
      chunkName: 'views-specialty',
      loader: () => import(/* webpackChunkName: "views-specialty" */ 'views/specialty-medication'),
    }),
  },
  {
    path: '/pot/initial',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-brxp',
      loader: () => import(/* webpackChunkName: "views-brxp" */ 'views/brxp'),
    }),
  },
  {
    path: '/nexus/initial',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-nexus',
      loader: () => import(/* webpackChunkName: "views-nexus" */ 'views/nexus'),
    }),
  },
  {
    path: '/edit-bsd-pharmacy',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-edit-bsd-pharmacy',
      loader: () =>
        import(/* webpackChunkName: "views-edit-bsd-pharmacy" */ 'views/medication-detailed-view/edit-bsd-pharmacy'),
    }),
  },
  {
    path: '/edit-magic-pharmacy',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-edit-magic-pharmacy',
      loader: () =>
        import(
          /* webpackChunkName: "views-edit-magic-pharmacy" */ 'views/medication-detailed-view/edit-magic-pharmacy'
        ),
    }),
  },
  {
    path: '/mdv/edit-pharmacy',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-edit-pharmacy',
      loader: () =>
        import(/* webpackChunkName: "views-edit-pharmacy" */ 'views/medication-detailed-view/edit-pharmacy'),
    }),
  },
  {
    path: '/drug-terms',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-drug-terms',
      loader: () => import(/* webpackChunkName: "views-drug-terms" */ 'views/medication-detailed-view/drug-terms'),
    }),
  },
  {
    path: '/about/:fulfillment',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-about-fulfillment',
      loader: () =>
        import(/* webpackChunkName: "views-about-fulfillment" */ 'views/medication-detailed-view/about-fulfillment'),
    }),
  },
  {
    path: '/(ls-gw|save|rxsave|rx|save10|offer|value|deal|savings|rx10|providers|new10|tenoff|invite-1|invite-2|account-2|fm10|amac10)',
    component: asyncComponentWithLoader({
      chunkName: 'views-marketing-gateway',
      loader: () => import(/* webpackChunkName: "views-marketing-gateway" */ 'views/marketing/gateway'),
    }),
  },
  {
    path: '/app',
    component: asyncComponentWithLoader({
      chunkName: 'views-download-app',
      loader: () => import(/* webpackChunkName: "views-download-app" */ 'views/download-app'),
    }),
  },
  {
    path: '/shareyourstory',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-share-your-story',
      loader: () => import(/* webpackChunkName: "views-share-your-story" */ 'views/share-your-story'),
    }),
  },
  {
    path: '/telesales-tou/:token',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-telesales-tou',
      loader: () => import(/* webpackChunkName: "views-telesales-tou" */ 'views/landing-pages/telesales-tou'),
    }),
  },
  {
    path: '/:slug/:info*',
    exact: true,
    component: asyncComponentWithLoader({
      chunkName: 'views-mdv',
      loader: () => import(/* webpackChunkName: "views-mdv" */ 'views/medication-detailed-view/routes'),
    }),
  },
];

/**
 * By default, every route is included.
 * If you want to make the route exist based
 * on a feature flag, use the `include` property.
 */
export default routes
  .filter((route) => route.include === undefined || route.include === true)
  .map(pick(['path', 'component', 'exact'])) as AsyncRouteProps[];
